import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useJobsQuery, useJobRunsQuery, useTriggerJobMutation } from '@/hooks/useJobs';
import { ApiError } from '@/api/client';
import type { JobSummaryDto, JobRunDto, JobSlug, JobRunStatus } from '@/api/types';
import './JobsTab.css';

const JOB_SLUGS: Record<JobSummaryDto['jobName'], JobSlug> = {
  OuCatalogSync: 'ou-catalog-sync',
  UserCatalogSync: 'user-catalog-sync',
};

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

function fmtDuration(durationMs: number): string {
  return durationMs < 1000 ? `${durationMs}ms` : `${(durationMs / 1000).toFixed(1)}s`;
}

function StatusBadge({ status, isRunning }: { status: JobRunStatus | null; isRunning: boolean }) {
  const { t } = useTranslation();

  if (isRunning) return <span className="jobs-badge jobs-badge--running">{t('jobs.statusRunning')}</span>;
  if (status === null) return <span className="jobs-badge jobs-badge--never">{t('jobs.statusNeverRun')}</span>;
  if (status === 'Success') return <span className="jobs-badge jobs-badge--success">{t('jobs.statusSuccess')}</span>;
  if (status === 'Skipped') return <span className="jobs-badge jobs-badge--skipped">{t('jobs.statusSkipped')}</span>;
  return <span className="jobs-badge jobs-badge--failed">{t('jobs.statusFailed')}</span>;
}

function JobHistoryTable({ jobSlug, expanded }: { jobSlug: JobSlug; expanded: boolean }) {
  const { t } = useTranslation();
  const { data: runs, isLoading, isError } = useJobRunsQuery(jobSlug, { enabled: expanded });

  if (!expanded) return null;
  if (isLoading) return <div className="jobs-history-loading">{t('jobs.historyLoading')}</div>;
  if (isError) return <div className="jobs-history-error">{t('jobs.historyError')}</div>;
  if (!runs || runs.length === 0) return <div className="jobs-history-empty">{t('jobs.historyEmpty')}</div>;

  return (
    <table className="jobs-history-table">
      <thead>
        <tr>
          <th>{t('jobs.colDate')}</th>
          <th>{t('jobs.colTrigger')}</th>
          <th>{t('jobs.colStatus')}</th>
          <th>{t('jobs.colDuration')}</th>
          <th>{t('jobs.colDetail')}</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run: JobRunDto) => (
          <tr key={run.id}>
            <td>{fmtDate(run.startedAt)}</td>
            <td>{run.triggerType === 'Manual' ? t('jobs.triggerManual') : t('jobs.triggerScheduled')}</td>
            <td><StatusBadge status={run.status} isRunning={false} /></td>
            <td>{fmtDuration(run.durationMs)}</td>
            <td>{run.errorMessage ?? run.skipReason ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function JobCard({ job }: { job: JobSummaryDto }) {
  const { t } = useTranslation();
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const triggerJob = useTriggerJobMutation();
  const jobSlug = JOB_SLUGS[job.jobName];
  const label = job.jobName === 'OuCatalogSync' ? t('jobs.ouCatalogSync') : t('jobs.userCatalogSync');

  return (
    <div className="jobs-card">
      <div className="jobs-card-header">
        <span className="jobs-card-name">{label}</span>
        <StatusBadge status={job.lastRun?.status ?? null} isRunning={job.isRunning} />
      </div>

      {job.lastRun && (
        <>
          <div className="jobs-card-row">
            <span>{t('jobs.lastRun')}</span>
            <strong>{fmtDate(job.lastRun.startedAt)} ({fmtDuration(job.lastRun.durationMs)})</strong>
          </div>
          {job.lastRun.status === 'Success' && (
            <div className="jobs-card-row">
              <span>{t('jobs.created')} / {t('jobs.updated')} / {t('jobs.unchanged')}</span>
              <strong><span>{job.lastRun.created}</span> / <span>{job.lastRun.updated}</span> / <span>{job.lastRun.unchanged}</span></strong>
            </div>
          )}
          {job.lastRun.status === 'Skipped' && (
            <div className="jobs-card-row">
              <span>{t('jobs.skipReason')}</span>
              <strong>{job.lastRun.skipReason}</strong>
            </div>
          )}
          {job.lastRun.status === 'Failed' && (
            <div className="jobs-card-error">{job.lastRun.errorMessage}</div>
          )}
        </>
      )}

      <div className="jobs-card-row">
        <span>{t('jobs.nextScheduled')}</span>
        <strong>{job.nextScheduledAt ? fmtDate(job.nextScheduledAt) : t('jobs.nextScheduledUnknown')}</strong>
      </div>

      <div className="jobs-card-actions">
        <button
          className="jobs-btn-run"
          type="button"
          disabled={job.isRunning || triggerJob.isPending}
          title={job.isRunning ? t('jobs.runningDisabled') : undefined}
          onClick={() => triggerJob.mutate(jobSlug)}
        >
          {t('jobs.runNow')}
        </button>
        <button
          className="jobs-btn-history"
          type="button"
          onClick={() => setHistoryExpanded(current => !current)}
        >
          {historyExpanded ? t('jobs.hideHistory') : t('jobs.viewHistory')}
        </button>
      </div>

      {triggerJob.isError && (
        <div className="jobs-card-error">
          {triggerJob.error instanceof ApiError && triggerJob.error.status === 409
            ? t('jobs.triggerConflict')
            : t('jobs.triggerError')}
        </div>
      )}

      <JobHistoryTable jobSlug={jobSlug} expanded={historyExpanded} />
    </div>
  );
}

export function JobsTab() {
  const { t } = useTranslation();
  const { data: jobs } = useJobsQuery();

  return (
    <div className="jobs-tab">
      <div className="jobs-header">
        <h2><i className="fas fa-sync" aria-hidden="true" /> {t('jobs.title')}</h2>
        <p className="jobs-subtitle">{t('jobs.subtitle')}</p>
      </div>

      <div className="jobs-grid">
        {(jobs ?? []).map(job => <JobCard key={job.jobName} job={job} />)}
      </div>
    </div>
  );
}
